import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router';
import {
  PLAYER_STATUSES,
  formatPence,
  type PlayerAction,
  type PlayerStatus,
  type PlayerSummary,
} from '@london-life/contracts';
import { Badge } from '@london-life/ui/components/badge';
import { Button } from '@london-life/ui/components/button';
import { Input } from '@london-life/ui/components/input';
import { Label } from '@london-life/ui/components/label';
import { Textarea } from '@london-life/ui/components/textarea';
import { DataTable } from '@london-life/ui/admin/data-table';
import { FormDialog } from '@london-life/ui/admin/form-dialog';
import { PageHeader } from '@london-life/ui/admin/page-header';
import { ErrorNotice } from '@london-life/ui/feedback/error-notice';
import { api } from '../lib/api';
import { useAdmin } from '../lib/admin-context';
import { when } from '../lib/format';
import { useRequest } from '../lib/use-request';

const statusBadge = (player: PlayerSummary) => (
  <Badge variant={player.status === 'active' ? 'secondary' : 'default'}>
    {PLAYER_STATUSES[player.status]}
    {player.isTest ? ' · test' : ''}
  </Badge>
);
const countryName = (code: string | null) =>
  code ? (new Intl.DisplayNames(['en-GB'], { type: 'region' }).of(code) ?? code) : '—';

type Dialog =
  | { kind: 'note' }
  | { kind: 'warn' }
  | { kind: 'suspend' }
  | {
      kind: 'reason';
      action: Exclude<PlayerAction['action'], 'note' | 'warn' | 'suspend' | 'mark_test'>;
      title: string;
      danger?: boolean;
    }
  | { kind: 'mark_test'; isTest: boolean };

const REASON_ACTIONS: Record<string, { title: string; description: string; danger?: boolean }> = {
  force_rename: {
    title: 'Remove the @username',
    description: 'The player must choose a new name next time they sign in; the old one is held for a year.',
  },
  sign_out_everywhere: {
    title: 'Sign out on every device',
    description: 'Every session ends now; they can sign in again.',
  },
  verify_email: { title: 'Mark the email as verified', description: 'Only when you have checked it another way.' },
  unsuspend: { title: 'Lift the suspension', description: 'The player can sign in straight away.' },
  ban: {
    title: 'Ban this player',
    description: 'Signed out everywhere and cannot sign in until an admin lifts the ban.',
    danger: true,
  },
  unban: { title: 'Lift the ban', description: 'The player can sign in straight away.' },
  delete: {
    title: 'Delete this account',
    description:
      'Personal details are erased and the life ends; money records stay without a name. This cannot be undone.',
    danger: true,
  },
};

function PlayerDrawer({ userId, onClose, onChanged }: { userId: string; onClose: () => void; onChanged: () => void }) {
  const { can } = useAdmin();
  const detail = useRequest(useCallback(() => api.admin.player(userId), [userId]));
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [text, setText] = useState('');
  const [days, setDays] = useState('7');
  const [message, setMessage] = useState<string | null>(null);
  const act = async (body: PlayerAction) => {
    const reply = await api.admin.actOnPlayer(userId, body);
    setMessage(reply.message);
    setText('');
    await detail.reload();
    onChanged();
  };
  const open = (next: Dialog) => {
    setText('');
    setMessage(null);
    setDialog(next);
  };
  const player = detail.data?.player;
  const held = player?.status === 'suspended' || player?.status === 'banned';
  return (
    <section className="admin-panel admin-player" aria-label="Player details">
      <div className="admin-player-head">
        <h2>{player ? (player.username ? `@${player.username}` : (player.email ?? 'Player')) : 'Player'}</h2>
        <Button type="button" variant="ghost" onClick={onClose}>
          Close
        </Button>
      </div>
      {detail.error && <ErrorNotice error={detail.error} onRetry={() => void detail.reload()} />}
      {message && (
        <p role="status" className="admin-muted">
          {message}
        </p>
      )}
      {detail.data && player && (
        <>
          <dl className="admin-facts">
            <dt>Status</dt>
            <dd>
              {statusBadge(player)}
              {player.suspendedUntil && player.status === 'suspended' && ` until ${when(player.suspendedUntil)}`}
              {player.moderationReason && held && <span className="admin-muted"> · {player.moderationReason}</span>}
            </dd>
            <dt>Display name</dt>
            <dd>{player.displayName ?? '—'}</dd>
            <dt>Email</dt>
            <dd>
              {player.email ?? '—'}{' '}
              {player.emailVerified ? <Badge variant="secondary">verified</Badge> : <Badge>not verified</Badge>}
            </dd>
            <dt>Country</dt>
            <dd>{countryName(player.country)}</dd>
            <dt>Joined</dt>
            <dd>{when(player.joinedAt)}</dd>
            <dt>Last seen</dt>
            <dd>{player.lastSeenAt ? when(player.lastSeenAt) : '—'}</dd>
            <dt>Signed in on</dt>
            <dd>
              {detail.data.openSessions} device{detail.data.openSessions === 1 ? '' : 's'}
            </dd>
            <dt>Character</dt>
            <dd>
              {player.life
                ? `${player.life.firstName} · wallet ${formatPence(player.life.walletPence)} · since ${when(player.life.startedAt)}`
                : 'none'}
            </dd>
          </dl>
          {player.status !== 'deleted' && can('players.support') && (
            <div className="admin-page-actions wrap">
              <Button type="button" variant="outline" onClick={() => open({ kind: 'note' })}>
                Add note
              </Button>
              <Button type="button" variant="outline" onClick={() => open({ kind: 'warn' })}>
                Warn
              </Button>
              {player.username && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => open({ kind: 'reason', action: 'force_rename', title: 'Remove @name' })}
                >
                  Remove @name
                </Button>
              )}
              <Button
                type="button"
                variant="outline"
                onClick={() => open({ kind: 'reason', action: 'sign_out_everywhere', title: 'Sign out everywhere' })}
              >
                Sign out everywhere
              </Button>
              {!player.emailVerified && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => open({ kind: 'reason', action: 'verify_email', title: 'Verify email' })}
                >
                  Verify email
                </Button>
              )}
              <Button
                type="button"
                variant="outline"
                onClick={() => open({ kind: 'mark_test', isTest: !player.isTest })}
              >
                {player.isTest ? 'Not a test account' : 'Mark as test account'}
              </Button>
              {can('players.ban') && (
                <>
                  {player.status === 'suspended' ? (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => open({ kind: 'reason', action: 'unsuspend', title: 'Lift suspension' })}
                    >
                      Lift suspension
                    </Button>
                  ) : (
                    player.status !== 'banned' && (
                      <Button type="button" variant="outline" onClick={() => open({ kind: 'suspend' })}>
                        Suspend
                      </Button>
                    )
                  )}
                  {player.status === 'banned' ? (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => open({ kind: 'reason', action: 'unban', title: 'Lift ban' })}
                    >
                      Lift ban
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      variant="destructive"
                      onClick={() => open({ kind: 'reason', action: 'ban', title: 'Ban', danger: true })}
                    >
                      Ban
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={() => open({ kind: 'reason', action: 'delete', title: 'Delete account', danger: true })}
                  >
                    Delete account
                  </Button>
                </>
              )}
            </div>
          )}

          <h3>Notes and warnings</h3>
          {detail.data.notes.length === 0 ? (
            <p className="admin-muted">None yet.</p>
          ) : (
            <ul className="admin-notes">
              {detail.data.notes.map((note) => (
                <li key={note.id}>
                  <Badge variant={note.kind === 'warning' ? 'default' : 'secondary'}>{note.kind}</Badge>
                  <span>{note.text}</span>
                  <small>
                    {note.by ? `@${note.by}` : 'staff'} · {when(note.createdAt)}
                    {note.kind === 'warning' && (note.seenAt ? ` · seen ${when(note.seenAt)}` : ' · not seen yet')}
                  </small>
                </li>
              ))}
            </ul>
          )}

          <h3>Recent activity</h3>
          <DataTable
            columns={[
              { key: 'at', header: 'When', render: (row) => when(row.at) },
              { key: 'what', header: 'What', render: (row) => `${row.kind}: ${row.target}` },
              {
                key: 'money',
                header: 'Money',
                render: (row) => (row.amountPence ? formatPence(row.amountPence) : '—'),
              },
            ]}
            rows={detail.data.recentActions}
            rowKey={(row) => `${row.at}-${row.target}`}
            empty="Nothing yet."
          />

          <h3>Account history</h3>
          <DataTable
            columns={[
              { key: 'at', header: 'When', render: (row) => when(row.createdAt) },
              { key: 'action', header: 'Action', render: (row) => row.action },
              { key: 'by', header: 'By', render: (row) => (row.by ? `@${row.by}` : '—') },
              {
                key: 'details',
                header: 'Details',
                render: (row) => (row.details ? JSON.stringify(row.details).slice(0, 120) : '—'),
              },
            ]}
            rows={detail.data.audit}
            rowKey={(row) => row.id}
            empty="Nothing yet."
          />
        </>
      )}

      <FormDialog
        open={dialog?.kind === 'note'}
        title="Add a note"
        description="Seen by admins only."
        submitLabel="Add note"
        canSubmit={text.trim().length >= 2}
        onSubmit={() => act({ action: 'note', text })}
        onClose={() => setDialog(null)}
      >
        <div className="admin-field">
          <Label htmlFor="player-note">Note</Label>
          <Textarea id="player-note" value={text} onChange={(event) => setText(event.target.value)} />
        </div>
      </FormDialog>
      <FormDialog
        open={dialog?.kind === 'warn'}
        title="Warn this player"
        description="Shown to the player once, the next time they open the game. Recorded in the audit log."
        submitLabel="Send warning"
        canSubmit={text.trim().length >= 5}
        onSubmit={() => act({ action: 'warn', text })}
        onClose={() => setDialog(null)}
      >
        <div className="admin-field">
          <Label htmlFor="player-warning">Warning</Label>
          <Textarea id="player-warning" value={text} onChange={(event) => setText(event.target.value)} />
        </div>
      </FormDialog>
      <FormDialog
        open={dialog?.kind === 'suspend'}
        title="Suspend this player"
        description="Signed out everywhere and cannot sign in until the suspension ends. The player sees the reason."
        submitLabel="Suspend"
        canSubmit={text.trim().length >= 5 && Number(days) >= 1}
        onSubmit={() => act({ action: 'suspend', days: Math.floor(Number(days)), reason: text })}
        onClose={() => setDialog(null)}
      >
        <div className="admin-field">
          <Label htmlFor="suspend-days">Days</Label>
          <Input
            id="suspend-days"
            type="number"
            min={1}
            max={365}
            value={days}
            onChange={(event) => setDays(event.target.value)}
          />
        </div>
        <div className="admin-field">
          <Label htmlFor="suspend-reason">Reason (shown to the player)</Label>
          <Textarea id="suspend-reason" value={text} onChange={(event) => setText(event.target.value)} />
        </div>
      </FormDialog>
      <FormDialog
        open={dialog?.kind === 'mark_test'}
        title={dialog?.kind === 'mark_test' && dialog.isTest ? 'Mark as a test account' : 'No longer a test account'}
        description="Test accounts are left out of player statistics."
        submitLabel="Save"
        canSubmit={text.trim().length >= 5}
        onSubmit={() =>
          act({ action: 'mark_test', isTest: dialog?.kind === 'mark_test' ? dialog.isTest : false, reason: text })
        }
        onClose={() => setDialog(null)}
      >
        <div className="admin-field">
          <Label htmlFor="test-reason">Reason</Label>
          <Textarea id="test-reason" value={text} onChange={(event) => setText(event.target.value)} />
        </div>
      </FormDialog>
      <FormDialog
        open={dialog?.kind === 'reason'}
        title={dialog?.kind === 'reason' ? REASON_ACTIONS[dialog.action]!.title : ''}
        description={dialog?.kind === 'reason' ? REASON_ACTIONS[dialog.action]!.description : ''}
        submitLabel={dialog?.kind === 'reason' ? dialog.title : 'Confirm'}
        canSubmit={text.trim().length >= 5}
        onSubmit={() =>
          dialog?.kind === 'reason' ? act({ action: dialog.action, reason: text } as PlayerAction) : Promise.resolve()
        }
        onClose={() => setDialog(null)}
      >
        <div className="admin-field">
          <Label htmlFor="action-reason">Reason</Label>
          <Textarea id="action-reason" value={text} onChange={(event) => setText(event.target.value)} />
          <p className="admin-muted">
            Recorded in the audit log
            {dialog?.kind === 'reason' &&
            (dialog.action === 'ban' || dialog.action === 'unsuspend' || dialog.action === 'unban')
              ? ' and shown to the player'
              : ''}
            .
          </p>
        </div>
      </FormDialog>
    </section>
  );
}

export function PlayersPage() {
  const [params, setParams] = useSearchParams();
  const q = params.get('q') ?? '';
  const status = params.get('status') ?? 'all';
  const page = Math.max(1, Number(params.get('page') ?? 1));
  const selected = params.get('player');
  const [draft, setDraft] = useState(q);
  useEffect(() => setDraft(q), [q]);
  const list = useRequest(useCallback(() => api.admin.players({ q: q || undefined, status, page }), [q, status, page]));
  const update = (next: Record<string, string | null>) => {
    const search = new URLSearchParams(params);
    for (const [key, value] of Object.entries(next)) {
      if (value) search.set(key, value);
      else search.delete(key);
    }
    setParams(search);
  };
  const submit = (event: FormEvent) => {
    event.preventDefault();
    update({ q: draft.trim() || null, page: null });
  };
  const pages = Math.max(1, Math.ceil((list.data?.total ?? 0) / 25));
  return (
    <section>
      <PageHeader
        title="Players"
        description="Look players up, see what happened on their account, and act when you need to. Every action is recorded."
      />
      <form className="admin-toolbar" onSubmit={submit}>
        <Input
          aria-label="Search players"
          placeholder="@username, email, display name or character"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
        />
        <select
          aria-label="Status"
          value={status}
          onChange={(event) => update({ status: event.target.value === 'all' ? null : event.target.value, page: null })}
        >
          <option value="all">All players</option>
          {(Object.keys(PLAYER_STATUSES) as PlayerStatus[]).map((key) => (
            <option key={key} value={key}>
              {PLAYER_STATUSES[key]}
            </option>
          ))}
        </select>
        <Button type="submit">Search</Button>
      </form>
      {list.error && <ErrorNotice error={list.error} onRetry={() => void list.reload()} />}
      <DataTable
        columns={[
          { key: 'name', header: 'Player', render: (row) => (row.username ? `@${row.username}` : (row.email ?? '—')) },
          { key: 'character', header: 'Character', render: (row) => row.life?.firstName ?? '—' },
          { key: 'wallet', header: 'Wallet', render: (row) => (row.life ? formatPence(row.life.walletPence) : '—') },
          { key: 'status', header: 'Status', render: statusBadge },
          { key: 'country', header: 'Country', render: (row) => countryName(row.country) },
          { key: 'joined', header: 'Joined', render: (row) => when(row.joinedAt) },
        ]}
        rows={list.data?.players ?? null}
        rowKey={(row) => row.id}
        loading={list.busy}
        empty="No players match."
        onRowClick={(row) => update({ player: row.id })}
      />
      <div className="admin-page-actions">
        <span className="admin-muted">
          {list.data ? `${list.data.total} player${list.data.total === 1 ? '' : 's'} · page ${page} of ${pages}` : ''}
        </span>
        <Button type="button" variant="outline" disabled={page <= 1} onClick={() => update({ page: String(page - 1) })}>
          Previous
        </Button>
        <Button
          type="button"
          variant="outline"
          disabled={page >= pages}
          onClick={() => update({ page: String(page + 1) })}
        >
          Next
        </Button>
      </div>
      {selected && (
        <PlayerDrawer userId={selected} onClose={() => update({ player: null })} onChanged={() => void list.reload()} />
      )}
    </section>
  );
}
